import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ProductType, ConsentState, FormInputSlide } from './types';

// Hooks
import { useSliderNavigation, useZipCodeCheck, useSavingsCalculator } from './hooks';

// Components
import ProgressBar from './components/ProgressBar';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../shared/consent/OptionalConsents';

// Utils
import { submitLead } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, getZipFromURL, splitFullName } from './utils/helpers';

/**
 * Slider_Partnership_Email - Componente principale del wizard di registrazione
 * Variante semplificata con form contatti e thank you page
 * Legge il CAP dall'URL e lo invia al database
 */
const SliderPartnershipEmail: React.FC = () => {
  // Form data state - Default product type to Photovoltaic
  const [formData, setFormData] = useState<FormData>({
    product_type: 'Photovoltaic',
  });
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Lead state
  const [mongoId, setMongoId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // ZIP from URL
  const [zipFromURL, setZipFromURL] = useState<string>('');

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  // Navigation hook
  const {
    currentSlide,
    progress,
    goToSlideById,
  } = useSliderNavigation(formData);

  // ZIP check hook
  const { zipFlag, checkZip } = useZipCodeCheck();

  // Savings calculator hook
  const { tooltipText } = useSavingsCalculator(
    formData.product_type as ProductType,
    zipFlag
  );

  // Track initial source and read ZIP from URL on mount
  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');

    // Read ZIP from URL
    const urlZip = getZipFromURL();
    if (urlZip) {
      setZipFromURL(urlZip);
      setFormData((prev) => ({ ...prev, zip: urlZip }));
      // Also check the ZIP code
      checkZip(urlZip);
    }
  }, [checkZip]);

  // Update form data with a new value
  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle form input change
  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  // Handle form submission for input slides
  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide) => {
      // Copy form values to form data
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      // Submit the lead since this is the only form
      await handleLeadSubmit();
    },
    [formValues, updateFormData]
  );

  // Submit lead to API
  const handleLeadSubmit = useCallback(async () => {
    setIsLoading(true);
    goToSlideById('loading');

    const uid = uuidv4();

    // Merge form values with form data, include ZIP from URL
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
      zip: zipFromURL || formData.zip || '',
      product_type: 'Photovoltaic', // Default product type
    };

    try {
      const id = await submitLead(finalFormData, uid, consentForApi);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        // Track lead with hashed email
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    } finally {
      setIsLoading(false);
    }
  }, [formData, formValues, consentForApi, goToSlideById, zipFromURL]);


  // Handle error retry
  const handleRetry = useCallback(() => {
    goToSlideById('contact_form');
  }, [goToSlideById]);

  // Render current slide content
  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;

        return (
          <div className={styles.slideBody}>
            {slide.subtitle && <p className={styles.wizardSubtitle}>{slide.subtitle}</p>}
            {slide.title && <h3 className={styles.wizardTitle}>{slide.title}</h3>}

            <FormInput
              inputs={slide.inputs}
              values={formValues}
              onChange={handleFormInputChange}
              onSubmit={() => handleFormSubmit(slide)}
              submitLabel="Invia richiesta"
              showBackButton={false}
              isPhoneSlide={true}
              optionalConsents={optionalConsents}
              onOptionalConsentsChange={setOptionalConsents}
            />
          </div>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <div className={styles.loadingSpinner} />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              mongoId={mongoId}
              phone={formData.phone}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderWrapper}>
          <ProgressBar progress={progress} />

          <div className={styles.wizSlide}>
            {renderSlideContent()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SliderPartnershipEmail;


